import React, { useEffect, useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements, CardElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import api from '@/lib/api';
import { trackCoachSignup, trackTrialStarted } from '@/lib/adsPixel';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import PasswordInput from '@/components/PasswordInput';
import { PortraitVideoCard } from '@/components/landing/PortraitVideoCard';
import { LegalFooter } from '@/components/LegalFooter';
import { useAuth } from '@/lib/auth.jsx';
import { toast } from 'sonner';
import {
    GraduationCap, Sparkles, CreditCard, ShieldCheck, Tag, CheckCircle2, X, Loader2,
    Phone, MessageSquare, ArrowRight,
} from 'lucide-react';

/**
 * Signup (Phase 57)
 * ---------------------------------------------------------------------------
 * One-screen instructor signup with all the friction up-front:
 *   1. Name + email + password + Terms (existing fields)
 *   2. SSU Community code — optional, always visible. Validates against
 *      /api/public/validate-promo without requiring auth.
 *   3. Stripe Elements card form — REQUIRED for everyone. SSU members
 *      with a 100%-off code still need a card on file so they can
 *      upgrade later without re-entering it.
 *
 * On submit we run a 4-step orchestration client-side:
 *   a) POST /auth/signup        — creates user + profile, sets JWT cookie
 *   b) stripe.createPaymentMethod(card)
 *   c) POST /instructor/billing/subscribe with payment_method_id + plan +
 *      promotion_code (the validated SSU code, if any)
 *   d) Navigate to /instructor/onboarding
 *
 * If step (a) succeeds but (c) fails, the user is still signed in — they
 * can retry from the Billing page. We surface the actual Stripe error
 * verbatim so they know what to fix.
 */

const PLAN_PRICES = { starter: 3999, pro: 7999 };

// Phase A2P (2026-07) — Twilio 10DLC mandated language. These strings are
// the exact copy Twilio's audit team expects to see on the signup form
// and in the consent record. Keep them in one place so the frontend text,
// the backend audit blob, and any future config-driven UI all agree.
const SMS_CONSENT_LABEL =
    "I agree to receive automated text messages from Splash Pad for account updates and registration alerts.";
const SMS_DISCLOSURE_TEXT =
    "By providing your phone number, you agree to receive recurring automated marketing and account text messages from Splash Pad. Consent is not a condition of any purchase. Msg and data rates may apply. Msg frequency varies. Reply HELP for help or STOP to cancel.";

// Loose but effective client-side phone validation: reject anything with
// fewer than 10 digits after stripping formatting. The backend re-checks
// this so a mutated client can't sneak past the Twilio compliance gate.
const isValidPhone = (raw) => (String(raw || '').match(/\d/g) || []).length >= 10;

// Phase 74y (2026-08) — Signup UX fix for the "silent gray button" trap.
// On mobile, Stripe's CardElement occasionally lags its onChange fire so
// `cardReady` stays false even after the coach has typed her card, which
// used to leave the submit button disabled with zero feedback about why.
// We now compute the FIRST unmet requirement and expose it as a helper
// line under the button — so the coach always knows what's missing.
// Order matches the submit-handler validation so the message the coach
// sees at rest is the same one they'd see on click.
//
// Password rules are kept in a single source of truth so the client-side
// hint EXACTLY matches the backend `enforce_password_policy` gate
// (common.py). If the backend adds/removes rules, update both together.
// The HaveIBeenPwned breach check is server-only and surfaces via
// backend error → the persistent inline banner under the password field.
const COMMON_PASSWORDS_BLOCKLIST = new Set([
    '1234567890', 'password123', 'qwerty1234', 'abcdefghij',
    'letmein123', 'iloveyou12', '1q2w3e4r5t', 'admin12345',
    'password1!', 'password!1', 'p@ssw0rd12', 'welcome123',
]);

const passwordPolicyIssue = (pw) => {
    if (!pw || pw.length < 10) return 'Password must be at least 10 characters.';
    if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw))
        return 'Password must include at least one letter and one number.';
    if (COMMON_PASSWORDS_BLOCKLIST.has(pw.toLowerCase()))
        return 'That password is too common — please choose a stronger one.';
    return '';
};

const firstMissingRequirement = ({
    first_name, last_name, email, password, phone, smsConsent, agreedLegal, cardReady, stripe,
}) => {
    if (!first_name?.trim()) return 'Enter your first name to continue.';
    if (!last_name?.trim()) return 'Enter your last name to continue.';
    if (!email?.trim()) return 'Enter your email to continue.';
    const pwIssue = passwordPolicyIssue(password);
    if (pwIssue) return pwIssue;
    if (!isValidPhone(phone)) return 'Enter a valid mobile phone number (10+ digits).';
    if (!smsConsent) return 'Check the SMS consent box to continue.';
    if (!agreedLegal) return 'Accept the Terms + Privacy Policy to continue.';
    if (!stripe) return 'Card form is still loading — one moment…';
    if (!cardReady) return 'Enter your card details to continue.';
    return '';
};

// Detects whether a backend error message is about the password so we
// can pin it as an inline banner under the password field (not just a
// toast that may auto-dismiss off-screen behind the mobile keyboard).
const isPasswordError = (msg) => {
    if (!msg || typeof msg !== 'string') return false;
    const m = msg.toLowerCase();
    return (
        m.includes('password')
        || m.includes('breach')
        || m.includes('pwned')
        || m.includes('too common')
    );
};

// Stripe.js loader is cached at module level so we don't refetch the
// publishable key on every render.
let stripePromise = null;
async function getStripePromise() {
    if (stripePromise) return stripePromise;
    const r = await api.get('/public/stripe-config');
    const pk = r.data?.publishable_key;
    if (!pk) throw new Error('Stripe is not configured on the server yet.');
    stripePromise = loadStripe(pk);
    return stripePromise;
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#0a1628',
            fontFamily: 'Inter, system-ui, sans-serif',
            '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#ff6b35' },
    },
    hidePostalCode: false,
};

function SignupInner() {
    const navigate = useNavigate();
    const { signup } = useAuth();
    const stripe = useStripe();
    const elements = useElements();
    const [params] = useSearchParams();
    // This form creates coach accounts; explicit parent links do not show
    // the coach commercial. Actual /coach/:slug/register is untouched.
    const signupRole = params.get('role');
    const showCoachCommercial = !signupRole || signupRole === 'instructor' || signupRole === 'coach';
    const planId = (params.get('plan') || 'starter').toLowerCase() === 'pro' ? 'pro' : 'starter';
    const planPriceCents = PLAN_PRICES[planId] || PLAN_PRICES.starter;

    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [first_name, setFirst] = useState('');
    const [last_name, setLast] = useState('');
    const [agreedLegal, setAgreedLegal] = useState(false);
    const [smsConsent, setSmsConsent] = useState(false);
    const [legalVersion, setLegalVersion] = useState('');
    const [cardReady, setCardReady] = useState(false);
    const [loading, setLoading] = useState(false);
    // Phase 74y — persistent inline error for password rejections coming
    // back from the backend (e.g. "appeared in a known data breach").
    // On mobile Safari, Sonner toasts can be pushed off-screen or
    // dismissed too quickly with the keyboard up — so we ALSO pin the
    // error under the password field until the coach edits it.
    const [passwordServerError, setPasswordServerError] = useState('');

    // SSU community code state
    const [codeInput, setCodeInput] = useState('');
    const [promo, setPromo] = useState(null);          // { valid, code, percent_off, message }
    const [promoError, setPromoError] = useState('');
    const [validatingPromo, setValidatingPromo] = useState(false);

    useEffect(() => {
        api.get('/public/legal').then((r) => setLegalVersion(r.data?.version || '')).catch(() => {
            /* non-fatal */
        });
    }, []);

    const validatePromo = async () => {
        const code = (codeInput || '').trim().toUpperCase();
        if (!code) { setPromoError('Enter a code first.'); return; }
        setValidatingPromo(true);
        setPromoError('');

        // Helper: accept the code "optimistically" — let the
        // /billing/subscribe call do the authoritative server-side check.
        // Worst case: Stripe rejects it at sub-create time and the
        // coach gets back on full price with a clear toast.
        const acceptOptimistic = (extra = {}) => setPromo({
            valid: true,
            code,
            percent_off: null,
            _unverified: true,
            message: "Code accepted — we'll verify it when your subscription starts.",
            ...extra,
        });

        try {
            // Hard 5s timeout so we never hang the form when the
            // validate endpoint is slow / unreachable.
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 5000);
            const r = await api.post('/public/validate-promo', { code }, { signal: controller.signal });
            clearTimeout(timer);
            const body = r?.data || {};
            if (body.valid) {
                setPromo(body);
                setPromoError('');
            } else if (body._server_error) {
                acceptOptimistic();
            } else {
                setPromo(null);
                setPromoError(body.message || "That code isn't valid.");
            }
        } catch (e) {
            // Any kind of network failure (timeout, 5xx, Cloudflare 520,
            // CORS quirk, etc) → accept the code optimistically.
            acceptOptimistic();
        } finally {
            setValidatingPromo(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setCodeInput('');
        setPromoError('');
    };

    // Derived price preview (in cents → dollars). When the code is
    // unverified (validate endpoint was unreachable), we can't know the
    // discount amount client-side, so we just show the gross price plus
    // a "pending verification" note — the real discount applies at
    // subscribe time.
    const effectiveCents = useMemo(() => {
        if (promo?.valid && !promo._unverified && promo?.percent_off != null) {
            return Math.max(0, Math.round(planPriceCents * (100 - Number(promo.percent_off)) / 100));
        }
        return planPriceCents;
    }, [planPriceCents, promo]);
    const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        // Phase 74y — mirror the backend `enforce_password_policy` client-side
        // via a single source of truth so the toast text + the inline banner
        // + the button-hint always agree. This lets us short-circuit before
        // the network round-trip on obvious failures (length, letter+digit,
        // common blocklist). The breach check remains server-only.
        const pwIssue = passwordPolicyIssue(password);
        if (pwIssue) {
            setPasswordServerError(pwIssue);
            toast.error(pwIssue, { duration: 8000 });
            try {
                const el = document.getElementById('pw');
                if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => el.focus(), 300); }
            } catch (_) { /* non-fatal */ }
            return;
        }
        // Phase A2P — Twilio 10DLC compliance gates. Both are required
        // for the Twilio brand+campaign audit; missing either one blocks
        // signup entirely (backend re-checks in case of a mutated client).
        if (!isValidPhone(phone)) {
            toast.error('Please enter a valid mobile phone number so we can send account and registration alerts.');
            return;
        }
        if (!smsConsent) {
            toast.error('Please check the SMS consent box to create an account.');
            return;
        }
        if (!agreedLegal) { toast.error('Please accept the Terms + Privacy Policy.'); return; }
        if (!stripe || !elements) { toast.error('Card form still loading — try again in a second.'); return; }
        if (!cardReady) { toast.error('Enter your card details to continue.'); return; }

        setLoading(true);
        try {
            // ---- 1) Create the account.
            const createdUser = await signup({
                email, password, role: 'instructor', first_name, last_name,
                legal_accepted_version: legalVersion,
                // Phase A2P — Twilio 10DLC opt-in payload. The backend
                // rejects with 400 if `sms_consent !== true` OR the phone
                // has fewer than 10 digits, so we can't accidentally ship
                // an audit-failing signup even if the checkbox state
                // desyncs on the client.
                phone,
                sms_consent: true,
                sms_consent_language: SMS_CONSENT_LABEL,
            });
            // ChatGPT Ads conversion — the account exists from here on, so
            // count the signup even if the card step below fails. No PII is
            // sent: just the event name + a pseudonymous event_id.
            trackCoachSignup(createdUser?.id);

            // ---- 2) Tokenise the card. We do this AFTER signup so the
            //         JWT cookie is in place for the /subscribe POST.
            const card = elements.getElement(CardElement);
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card', card,
            });
            if (pmError) {
                toast.error(pmError.message || 'Your card was rejected by Stripe.');
                // Account is created; punt to onboarding so they can retry from Billing.
                navigate('/instructor/onboarding', { replace: true });
                return;
            }

            // ---- 3) Subscribe (trial_period_days=30 set server-side).
            //         If they entered a valid code we pass it along; the
            //         backend will attach it as `discounts[]` on the
            //         Stripe Subscription so it applies forever.
            try {
                const r = await api.post('/instructor/billing/subscribe', {
                    plan_id: planId,
                    payment_method_id: paymentMethod.id,
                    promotion_code: promo?.valid ? promo.code : undefined,
                });
                if (r.data?.requires_action && r.data?.payment_intent_client_secret) {
                    const secret = r.data.payment_intent_client_secret;
                    const cr = secret.startsWith('seti_')
                        ? await stripe.confirmCardSetup(secret)
                        : await stripe.confirmCardPayment(secret);
                    if (cr?.error) {
                        toast.error(cr.error.message || 'Bank declined the authentication.');
                    }
                }
                if (r.data?.card_declined) {
                    toast.error('Your card was declined — try a different card from Billing.');
                } else {
                    // ChatGPT Ads — 30-day trial subscription created.
                    trackTrialStarted(planId, createdUser?.id);
                }
            } catch (subErr) {
                const msg = subErr?.response?.data?.detail || subErr?.message || 'Subscription failed';
                toast.error(`Account created, but subscription couldn't be set up: ${msg}. Retry from Billing.`, { duration: 8000 });
            }

            toast.success('Welcome to SplashPad — let\u2019s set up your school!');
            navigate('/instructor/onboarding', { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Signup failed';
            if (typeof msg === 'string' && msg.toLowerCase().includes('email already')) {
                toast.error('That email is already registered.', {
                    description: 'Try logging in instead, or use a different email.',
                    action: { label: 'Log in', onClick: () => navigate(`/login?email=${encodeURIComponent(email)}`) },
                    duration: 8000,
                });
            } else if (isPasswordError(msg)) {
                // Phase 74y — pin password errors inline (not just toast)
                // so mobile users don't miss them when the on-screen
                // keyboard pushes Sonner toasts off the viewport. The
                // banner clears as soon as the coach edits the password.
                setPasswordServerError(msg);
                toast.error(msg, { duration: 10000 });
                // Best-effort scroll + focus the password field so the
                // coach can immediately fix it — critical on mobile
                // where the submit button may be below the fold.
                try {
                    const el = document.getElementById('pw');
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => el.focus(), 300);
                    }
                } catch (_) { /* non-fatal */ }
            } else {
                toast.error(msg, { duration: 6000 });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-dvh flex-col bg-background text-foreground">
            <div className="px-4 py-4 sm:px-6">
                <Link to="/"><Logo /></Link>
            </div>
            <div className={`mx-auto grid w-full flex-1 grid-cols-1 items-start gap-8 px-4 py-6 pb-32 sm:px-6 sm:pb-12 ${showCoachCommercial ? 'max-w-6xl lg:grid-cols-12' : 'max-w-xl'}`}>
                {showCoachCommercial && <aside className="min-w-0 lg:sticky lg:top-6 lg:col-span-5" aria-labelledby="signup-si-heading" data-testid="signup-si-video-panel">
                    <div className="sp-card relative overflow-hidden p-5 sm:p-6">
                        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[var(--sp-cyan)]/15 blur-3xl" />
                        <div className="relative flex items-end justify-between gap-3">
                            <div className="min-w-0">
                                <p className="sp-eyebrow" data-testid="signup-si-eyebrow">Included with your trial</p>
                                <h2 id="signup-si-heading" className="mt-2 font-display text-2xl font-extrabold leading-[1.05] tracking-[-0.02em] text-[color:var(--sp-text)] sm:text-3xl" data-testid="signup-si-heading">Meet Splash,<br />your SI agent.</h2>
                            </div>
                            <img src="/mascot/splash-pointing.png" alt="" aria-hidden="true" width="120" height="180" className="w-[72px] shrink-0 drop-shadow-[0_12px_24px_rgba(10,22,40,0.25)] sm:w-[88px]" />
                        </div>
                        <p className="relative mt-3 text-sm leading-relaxed text-[color:var(--sp-text-muted)]" data-testid="signup-si-description">Describe a task. Splash previews it. You confirm.</p>
                        <ul className="relative mt-3 space-y-1.5 text-sm text-[color:var(--sp-text-muted)]" data-testid="signup-si-bullets">
                            {['Create classes and packages by describing them', 'Enroll or move swimmers with a seat check', 'Nothing changes until you confirm'].map((b) => (
                                <li key={b} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00b87a]" aria-hidden="true" />{b}</li>
                            ))}
                        </ul>
                        <div className="relative mt-5">
                            <PortraitVideoCard testId="signup-si-commercial" size="sm" />
                        </div>
                        <p className="relative mt-4 flex items-start gap-2 text-xs leading-relaxed text-[color:var(--sp-text-subtle)]" data-testid="signup-si-confirmation-note"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />Preview first. Confirm when ready. Splash never applies changes just because you asked.</p>
                        <Button asChild type="button" variant="outline" className="relative mt-4 min-h-11 w-full rounded-xl sd-focus lg:hidden" data-testid="signup-skip-to-form-link"><a href="#coach-signup-form" onClick={(event) => { event.preventDefault(); const input = document.getElementById('fn'); input?.scrollIntoView({ block: 'center', behavior: 'auto' }); input?.focus({ preventScroll: true }); }}>Skip to signup form <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
                    </div>
                </aside>}
                <Card id="coach-signup-form" className={`sd-card w-full min-w-0 scroll-mt-6 p-5 sm:p-8 ${showCoachCommercial ? 'lg:col-span-7' : ''}`} data-testid="signup-card">
                    {/* Phase 74b (2026-07) — "Are you a parent?" prevention
                        banner. Coach reported that at least one parent
                        accidentally signed up as an instructor (they landed
                        on this page from a marketing CTA thinking it was
                        the way to book lessons). Parents don't register here
                        at all — they register through their coach's link
                        (e.g. /pool/kerry-mourning/register). This banner
                        makes that crystal-clear BEFORE they fill out the
                        form. Gold/warm tint so it draws the eye without
                        looking like an error state. */}
                    <div
                        className="mb-4 flex items-start gap-2 rounded-xl border border-[#ffd700]/50 bg-[#fffbeb] p-3 text-xs sm:text-sm"
                        data-testid="signup-parent-prevention-banner"
                    >
                        <span aria-hidden="true" className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full bg-[#ffb800]" />
                        <div className="min-w-0">
                            <div className="font-semibold text-[#7a5b00]">
                                Are you a parent looking to book swim lessons?
                            </div>
                            <div className="mt-0.5 text-[#7a5b00]/90">
                                This form creates a <strong>coach</strong> account. Parents register through their swim coach&apos;s personal SplashPad page — ask your coach for their booking link. You can also{' '}
                                <Link to="/find-instructor" className="font-semibold underline hover:no-underline" data-testid="signup-find-coach-link">
                                    find your coach here
                                </Link>.
                            </div>
                        </div>
                    </div>

                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#00d4ff]/10 px-3 py-1 text-xs font-semibold text-[#0a1628]">
                        <GraduationCap className="h-3.5 w-3.5" /> For swim instructors & schools
                    </div>
                    <h1 className="font-display text-2xl font-bold text-[#0a1628]">Create your SplashPad account</h1>
                    <p className="mt-1 text-sm text-[#1a3a5c]/80">
                        30-day free trial on the <strong className="capitalize">{planId}</strong> plan ({dollars(planPriceCents)}/mo).
                        SSU Community members: enter your code below for 100% off forever.
                    </p>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
                        {/* === Identity === */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="fn">First name</Label>
                                <Input id="fn" value={first_name} onChange={(e) => setFirst(e.target.value)} required className="mt-1 h-11 rounded-xl" data-testid="signup-firstname-input" />
                            </div>
                            <div>
                                <Label htmlFor="ln">Last name</Label>
                                <Input id="ln" value={last_name} onChange={(e) => setLast(e.target.value)} required className="mt-1 h-11 rounded-xl" data-testid="signup-lastname-input" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="em">Email</Label>
                            <Input id="em" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="signup-email-input" />
                        </div>

                        {/* === Phase A2P (2026-07) — Twilio 10DLC compliance ===
                            Mobile phone is now MANDATORY at signup. Without a
                            real number + an explicit opt-in checkbox below,
                            Twilio's A2P audit rejects the brand + campaign
                            registration and no outbound SMS can flow. Field
                            is placed directly above the password field per
                            the audit spec, followed immediately by the
                            small-print disclosure Twilio requires. */}
                        <div data-testid="signup-phone-block">
                            <Label htmlFor="ph" className="flex items-center gap-1.5">
                                <Phone className="h-3.5 w-3.5 text-[#1a3a5c]/70" aria-hidden="true" />
                                Mobile phone number
                            </Label>
                            <Input
                                id="ph"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                required
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="(555) 123-4567"
                                className="mt-1 h-11 rounded-xl"
                                data-testid="signup-phone-input"
                                aria-describedby="signup-phone-disclosure"
                            />
                            <p
                                id="signup-phone-disclosure"
                                className="mt-2 text-[11px] leading-relaxed text-[#1a3a5c]/70"
                                data-testid="signup-sms-disclosure"
                            >
                                {SMS_DISCLOSURE_TEXT}
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="pw">Password</Label>
                            <PasswordInput
                                id="pw"
                                required
                                minLength={10}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    // Phase 74y — clear any server-side password
                                    // error banner as soon as the coach starts
                                    // editing, so it doesn't linger stale.
                                    if (passwordServerError) setPasswordServerError('');
                                }}
                                className="mt-1 h-11 rounded-xl"
                                data-testid="signup-password-input"
                                testIdShow="signup-password-toggle"
                                aria-invalid={Boolean(passwordServerError) || undefined}
                                aria-describedby={passwordServerError ? 'pw-server-error' : 'pw-help'}
                            />
                            {/* Phase 74y — persistent inline password error.
                                Shows the exact backend rejection (e.g. breach
                                or common-password) so mobile users see it
                                even when Sonner toasts get pushed off-screen
                                by the on-screen keyboard. */}
                            {passwordServerError ? (
                                <div
                                    id="pw-server-error"
                                    role="alert"
                                    className="mt-2 rounded-lg border border-[#ff6b35]/40 bg-[#ff6b35]/10 px-3 py-2 text-xs font-medium text-[#c14320]"
                                    data-testid="signup-password-server-error"
                                >
                                    {passwordServerError}
                                </div>
                            ) : null}
                            <p id="pw-help" className="mt-1 text-xs text-[#1a3a5c]/70">At least 10 characters, with a letter and a number. Avoid passwords you have used elsewhere.</p>
                        </div>

                        {/* === SSU Community code === */}
                        <div className="rounded-xl border border-[#19c37d]/25 bg-[#19c37d]/5 p-3" data-testid="signup-promo-box">
                            <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#19c37d]">
                                <Tag className="h-3.5 w-3.5" /> SSU Community code (optional)
                            </Label>
                            {promo?.valid ? (
                                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-white p-2.5">
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle2 className={`h-4 w-4 ${promo._unverified ? 'text-[#f59e0b]' : 'text-[#19c37d]'}`} />
                                        <span className="font-semibold text-[#0a1628]">{promo.code}</span>
                                        <span className="text-xs text-[#1a3a5c]/70">
                                            {promo._unverified
                                                ? '— we\u2019ll check this at signup'
                                                : `— ${promo.percent_off}% off forever`}
                                        </span>
                                    </div>
                                    <button type="button" onClick={clearPromo} className="text-xs text-[#1a3a5c]/70 hover:text-[#0a1628]" data-testid="signup-promo-clear">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-2 flex gap-2">
                                    <Input
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                        placeholder="Enter your community code"
                                        className="h-10 rounded-lg uppercase"
                                        autoComplete="off"
                                        data-testid="signup-promo-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={validatePromo}
                                        disabled={validatingPromo || !codeInput}
                                        className="h-10 shrink-0 rounded-lg border-[#19c37d]/40 text-[#19c37d] hover:bg-[#19c37d]/10"
                                        data-testid="signup-promo-apply"
                                    >
                                        {validatingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                                    </Button>
                                </div>
                            )}
                            {promoError && (
                                <p className="mt-1.5 text-xs text-[#ff6b35]" data-testid="signup-promo-error">{promoError}</p>
                            )}
                            <p className="mt-1.5 text-[11px] text-[#1a3a5c]/70">
                                Don't have a code? <a href="https://www.skool.com/superheroswimuniversity/about?ref=2eba9baa063e4d71916d29f262f0acd2" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#19c37d] hover:underline">Join the SSU Community →</a>
                            </p>
                        </div>

                        {/* R11: the commercial is beside this form (stacked on
                            mobile), so checkout never competes with a second video. */}

                        {/* === Card === */}
                        <div className="rounded-xl border border-[#0a1628]/15 bg-white p-3" data-testid="signup-card-box">
                            <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#1a3a5c]/70">
                                <CreditCard className="h-3.5 w-3.5" /> Card on file
                            </Label>
                            <div className="mt-2 rounded-lg border border-[#0a1628]/10 bg-[#f4f7fb] px-3 py-3" data-testid="signup-card-element-wrapper">
                                <CardElement options={CARD_ELEMENT_OPTIONS} onChange={(e) => setCardReady(e.complete && !e.error)} />
                            </div>
                            <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#1a3a5c]/70">
                                <ShieldCheck className="h-3 w-3" />
                                Card stays on file for easy upgrade. <strong className="ml-1">No charges during your 30-day trial.</strong>
                                {promo?.valid && Number(promo.percent_off) === 100
                                    ? <span className="ml-1">As long as your <strong>{promo.code}</strong> code stays valid, you'll never be charged.</span>
                                    : null}
                            </p>
                        </div>

                        {/* === Price summary === */}
                        <div className="rounded-xl bg-[#0a1628]/5 p-3 text-sm" data-testid="signup-price-summary">
                            <div className="flex items-center justify-between">
                                <span className="text-[#1a3a5c]">{planId === 'pro' ? 'Pro' : 'Starter'} plan, after 30-day trial</span>
                                <span className="font-semibold text-[#0a1628]">
                                    {effectiveCents === 0 ? (
                                        <span className="text-[#19c37d]">FREE</span>
                                    ) : effectiveCents !== planPriceCents ? (
                                        <>
                                            <span className="mr-1 text-[#1a3a5c]/60 line-through">{dollars(planPriceCents)}</span>
                                            <span className="text-[#19c37d]">{dollars(effectiveCents)}/mo</span>
                                        </>
                                    ) : (
                                        <>{dollars(planPriceCents)}/mo</>
                                    )}
                                </span>
                            </div>
                            {/* Phase 117f — auto-renewal disclosure (state ARL laws: clear +
                                conspicuous before purchase, with the cancel path). */}
                            <p className="mt-2 border-t border-[#0a1628]/10 pt-2 text-[11px] leading-snug text-[#1a3a5c]/80" data-testid="signup-auto-renew-disclosure">
                                {effectiveCents === 0
                                    ? <>After your 30-day trial your plan continues month to month at <strong>{dollars(effectiveCents)}/mo</strong> while your code is valid, and renews automatically until you cancel.</>
                                    : <>After your 30-day trial, your card is charged <strong>{dollars(effectiveCents)}/mo</strong> and your plan <strong>renews automatically each month until you cancel</strong>.</>}
                                {' '}Cancel anytime under Your Subscription — access continues through the period you’ve paid for.
                            </p>
                        </div>

                        {/* === Legal === */}
                        {/* Phase A2P — Twilio 10DLC MANDATORY opt-in checkbox.
                            Starts UNCHECKED (`false` default state). The user
                            must actively check it. Both the frontend submit
                            handler AND the backend /auth/signup endpoint
                            reject signups where sms_consent is false, so the
                            audit trail is defense-in-depth. */}
                        <label
                            className="flex cursor-pointer items-start gap-2 rounded-xl border border-[#00d4ff]/40 bg-[#00d4ff]/5 p-3 text-xs text-[#0a1628]"
                            data-testid="signup-sms-consent-row"
                        >
                            <Checkbox
                                checked={smsConsent}
                                onCheckedChange={(v) => setSmsConsent(v === true)}
                                data-testid="signup-sms-consent-checkbox"
                                className="mt-0.5"
                                aria-required="true"
                            />
                            <span className="flex-1">
                                <span className="flex items-center gap-1.5 font-semibold text-[#0a1628]">
                                    <MessageSquare className="h-3.5 w-3.5 text-[#1a3a5c]/70" aria-hidden="true" />
                                    {SMS_CONSENT_LABEL}
                                </span>
                                <span className="mt-1 block text-[11px] text-[#1a3a5c]/70">
                                    Required to create an account. You can reply <strong>STOP</strong> to any text to opt out.
                                </span>
                            </span>
                        </label>

                        <label
                            className="flex cursor-pointer items-start gap-2 rounded-xl border border-[#0a1628]/15 bg-[#f4f7fb] p-3 text-xs text-[#0a1628]"
                            data-testid="signup-legal-row"
                        >
                            <Checkbox
                                checked={agreedLegal}
                                onCheckedChange={(v) => setAgreedLegal(v === true)}
                                data-testid="signup-legal-checkbox"
                                className="mt-0.5"
                            />
                            <span>
                                I have read and agree to SplashPad's{' '}
                                <Link to="/privacy" target="_blank" className="font-semibold underline" data-testid="signup-privacy-link">Privacy Policy</Link>
                                {' '}and{' '}
                                <Link to="/terms" target="_blank" className="font-semibold underline" data-testid="signup-terms-link">Terms of Service</Link>.
                                {legalVersion && <span className="ml-1 text-[#1a3a5c]/60">(v{legalVersion})</span>}
                            </span>
                        </label>

                        <Button
                            type="submit"
                            /* Phase 74y — button stays clickable at rest so the
                               `submit` handler can surface a specific per-field
                               toast (previously it was disabled by any missing
                               gate, which trapped mobile users behind a silent
                               gray button when Stripe's CardElement onChange
                               didn't fire reliably). We only disable during the
                               in-flight submit to prevent double-clicks. */
                            disabled={loading}
                            className="sd-cta sd-focus h-11 w-full rounded-xl"
                            data-testid="signup-submit-button"
                        >
                            {loading
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account…</>
                                : 'Start my 30-day free trial'}
                        </Button>
                        {/* Phase 74y — proactive helper line: shows the FIRST
                            unmet requirement so the coach always knows what
                            still needs attention. Hidden once the form is
                            fully valid or while the submit is in flight. */}
                        {!loading && (() => {
                            const hint = firstMissingRequirement({
                                first_name, last_name, email, password, phone,
                                smsConsent, agreedLegal, cardReady, stripe,
                            });
                            return hint ? (
                                <p
                                    className="-mt-1 text-center text-xs text-[#ff6b35]"
                                    data-testid="signup-submit-hint"
                                    role="status"
                                    aria-live="polite"
                                >
                                    {hint}
                                </p>
                            ) : null;
                        })()}
                    </form>
                    <div className="mt-4 text-sm text-[#1a3a5c]">
                        Already have an account? <Link to="/login" className="font-semibold text-[#0a1628] hover:underline">Log in</Link>
                    </div>
                    <div className="mt-6 rounded-xl border border-dashed border-[#1a3a5c]/15 bg-[#f4f7fb] p-3 text-xs text-[#1a3a5c]/80">
                        <Sparkles className="mr-1 inline h-3.5 w-3.5 text-[#ffd700]" />
                        <strong>Looking to register your child?</strong> Parents register directly through their swim instructor's personal SplashPad link. Ask your coach for their registration page or embed code.
                    </div>

                    {/* Phase A2P — Twilio audit requires Terms + Privacy
                        links to be plainly visible at the bottom of the
                        signup card in addition to inside the consent
                        checkbox above. Kept in a compact single-line row
                        so the card doesn't feel bottom-heavy. */}
                    <div
                        className="mt-6 border-t border-[#0a1628]/10 pt-4 text-center text-xs text-[#1a3a5c]/80"
                        data-testid="signup-legal-footer"
                    >
                        <Link
                            to="/terms"
                            target="_blank"
                            className="font-semibold text-[#0a1628] underline hover:no-underline"
                            data-testid="signup-terms-footer-link"
                        >
                            Terms of Service
                        </Link>
                        <span className="mx-2 text-[#1a3a5c]/40" aria-hidden="true">·</span>
                        <Link
                            to="/privacy"
                            target="_blank"
                            className="font-semibold text-[#0a1628] underline hover:no-underline"
                            data-testid="signup-privacy-footer-link"
                        >
                            Privacy Policy
                        </Link>
                    </div>
                </Card>
            </div>
            <LegalFooter />
        </div>
    );
}

// Wrapper that loads Stripe.js once at mount.
export default function Signup() {
    const [stripeP, setStripeP] = useState(null);
    useEffect(() => {
        getStripePromise().then(setStripeP).catch((e) => {
            toast.error(e?.message || 'Stripe failed to load.');
        });
    }, []);
    if (!stripeP) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f4f7fb] text-sm text-[#1a3a5c]" data-testid="signup-loading">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading checkout…
            </div>
        );
    }
    return (
        <Elements stripe={stripeP}>
            {/* SEO audit fix — /signup is a form-only page (thin content) and is
                already Disallowed in robots.txt. Mark it noindex so crawlers
                that reach it via internal links don't flag it as low-quality
                or duplicate content. Title stays unique for the browser tab. */}
            <Seo
                title="Start your free 30-day trial | SplashPad"
                description="Create your SplashPad coach account: branded registration pages, digital waivers, and automated Stripe payments for swim instructors. Free 30-day trial, cancel anytime."
                noIndex
            />
            <SignupInner />
        </Elements>
    );
}
